import React, { useEffect, useState } from "react";
import FilterBar from '../../components/amazon/FilterBar';
import WalmartFilterBar from "../../components/walmart/WalmartFilterBar";
import BothProductsTable from "./BothProductsTable";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinearProgress from '@mui/material/LinearProgress';
import { Box, Button, FormControl, InputLabel, MenuItem, Modal, Select, Typography } from "@mui/material";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useNavigate, useLocation } from "react-router-dom";
import { addScanedFile, addScanResultsToDB, calcRefFees, calculateWfs } from "../../utils/helpers";
import { getUser } from "../../utils/authService";

function BothMarketplaceScan(props) {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [subscribed, setSubscribed] = useState(false);
  const [alertModalMsg, setAlertModalMsg] = useState("");
  const [navigatePage, setNavigatePage] = useState("");
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [invalidColumns, setInvalidColumns] = useState({ identifier: false, price: false });

  // Amazon state
  const [azFilters, setAzFilters] = useState({});
  const [azData, setAzData] = useState([]);
  const [azSortASC, setAzSortASC] = useState(false);

  // Walmart state
  const [wmFilters, setWmFilters] = useState({});
  const [wmData, setWmData] = useState([]);
  const [wmSortASC, setWmSortASC] = useState(false);

  const [azFilteredData, setAzFilteredData] = useState([]);
  const [wmFilteredData, setWmFilteredData] = useState([]);

  // Common state
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [openColumnMappingModal, setOpenColumnMappingModal] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [currentFile, setCurrentFile] = useState(null);
  const [saveResult, setSaveResult] = useState(false);
  const [csvRows, setCsvRows] = useState(0);

  let lastUpdated = Date.now();

  // Both scan can only use Barcode as identifier
  const identifier = "Barcode";

  const capitalizeKeys = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (key.toLocaleLowerCase().includes("barcode") || key.toLocaleLowerCase().includes("upc") || key.toLocaleLowerCase().includes("ean")) {
        acc["Barcode"] = obj[key];
        return acc;
      }
      if (key.toLocaleLowerCase().includes("asin")) {
        acc["ASIN"] = obj[key];
        return acc;
      }
      if (key.toLocaleLowerCase().includes("item id") || key.toLocaleLowerCase().includes("itemid")) {
        acc["Item ID"] = obj[key];
        return acc;
      }
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      acc[capitalizedKey] = obj[key];
      return acc;
    }, {});
  };

  const sendFile = async (data_l) => {
    // Process data_l in batches with delay
    const batchSize = 10;
    let index = 0;

    // Step 1: Filter out items missing Barcode or Price
    const validItems = data_l.filter(item => {
      const findKey = (target) =>
        Object.keys(item).find(key => key.trim().toLowerCase() === target.toLowerCase());

      const identifierKey = findKey(identifier);
      const priceKey = findKey("cost") || findKey("price");

      return identifierKey && item[identifierKey] && priceKey && item[priceKey];
    });

    if (validItems.length === 0) {
      alert("Aniseller couldn't find any valid rows. Please make sure you're using the template we provided. If you need help, we're available anytime.")
    }

    // Step 2: Build a map to keep lowest price per identifier
    const idMap = new Map();

    validItems.forEach(item => {
      const id = item[identifier];
      const price = parseFloat(item.Price);

      if (!idMap.has(id) || price < parseFloat(idMap.get(id).Price)) {
        idMap.set(id, { ...item });
      }
    });

    // Step 3: Convert back to an array
    const uniqueItems = Array.from(idMap.values());

    // Step 4: Set amount of csv rows to display it
    setCsvRows(uniqueItems.length)

    // Step 5: Start the process by batches on uniqueItems
    async function processBatch() {
      if (index < uniqueItems.length && window.location.href.includes("csvscan")) {
        setIsProductsLoading(true);
        const batch = uniqueItems.slice(index, index + batchSize).map(item => {
          const findKey = (target) =>
            Object.keys(item).find(key => key.trim().toLowerCase() === target.toLowerCase());

          const identifierKey = findKey(identifier);
          const priceKey = findKey("cost") || findKey("price");
          const nameKey = findKey("name");
          const imageKey = findKey("image");

          let identifierValue = item[[identifierKey]].includes("E+") ? Number(item[[identifierKey]]).toFixed(0).toString() : item[[identifierKey]];
          identifierValue = identifierValue.padStart(12, '0');

          return {
            [identifier]: identifierValue.replaceAll("-", "").replaceAll("'", ""),
            "Price": item[[priceKey]] ? item[[priceKey]].replace("$", "").replaceAll(" ", "") : -1,
            "Name": item[[nameKey]],
            "Image": item[[imageKey]]
          }
        });

        const requestOptions = {
          method: "POST",
          mode: "cors",
          headers: {
            Accept: "application/json; charset=utf8",
            "Content-Type": "application/json; charset=utf8",
          },
          body: JSON.stringify({
            csv_file: batch,
            identifier: identifier,
            email: getUser()
          }),
        };

        try {
          // Run both API calls concurrently using Promise.all
          Promise.all([
            fetch("https://server.getmarter.com/upload_scanner_csv_aniseller_walmart/", requestOptions)
              .then(response => response.json())
              .catch(e => {
                console.error("Walmart API error:", e);
                return { error: true, source: "walmart" };
              }),
            fetch("https://server.nepeto.com/upload_scanner_csv_aniseller/", requestOptions)
              .then(response => response.json())
              .catch(e => {
                console.error("Amazon API error:", e);
                return { error: true, source: "amazon" };
              })
          ])
            .then(([walmartData, amazonData]) => {
              // Handle permission issues first
              if (walmartData === "No permissions" || amazonData === "No permissions") {
                navigate("/pricing");
                return;
              }

              // Process Walmart data
              if (!walmartData.error) {
                // Clean all barcodes by removing non-digit characters
                const cleanedWalmartData = walmartData.map(item => {
                  if (item.Barcode) {
                    // Remove all non-digit characters from Barcode
                    const cleanedBarcode = item.Barcode.replace(/\D/g, '');
                    return {
                      ...item,
                      Barcode: cleanedBarcode,
                      // If upcs exists, also clean it
                      upcs: item.upcs ? item.upcs.split(',').map(upc => upc.replace(/\D/g, '')).join(',') : undefined
                    };
                  }
                  return item;
                });

                setWmData(prevData => {
                  // Process and calculate profit and ROI for each item in the new Walmart data
                  const processedData = cleanedWalmartData.map(product => {
                    // Clone the product to avoid modifying the original
                    const processedProduct = { ...product };
                    const wfsCost = calculateWfs(product?.title, product.height, product.width, product.length, product.weight, product.walmart_price, product.category);

                    // Calculate profit
                    const costPrice = product.price;
                    const shippingCost = (!wmFilters.hasOwnProperty("isWFS") || !wmFilters.isWFS) ? wfsCost : parseFloat(localStorage.getItem("shippingCost") || 0);
                    const profit = product.walmart_price - costPrice - shippingCost - calcRefFees(product.category, product.walmart_price, product.sub_category) - parseFloat(localStorage.getItem("costPrep") || 0);

                    // Calculate ROI
                    const investment = (product.price * 1) + parseFloat(localStorage.getItem("costPrep") || 0);
                    const roi = (profit / investment) * 100;

                    // Store the calculated values
                    processedProduct.profit = profit;
                    processedProduct.roi = roi;

                    return processedProduct;
                  });

                  // Sort the processed data
                  const sortedData = [...prevData, ...processedData.sort((a, b) => {
                    if (a.title === "" && b.title !== "") return 1; // Move empty title to the end
                    if (a.title !== "" && b.title === "") return -1; // Keep non-empty title at the front
                    return 0; // Keep order for titles that are both empty or both non-empty
                  })];

                  return sortedData;
                });
              }

              // Process Amazon data
              if (!amazonData.error) {
                // Clean all barcodes by removing non-digit characters
                const cleanedAmazonData = amazonData.map(item => {
                  let cleanedItem = { ...item };

                  // Clean the main Barcode field
                  if (cleanedItem.Barcode) {
                    cleanedItem.Barcode = cleanedItem.Barcode.replace(/\D/g, '');
                  }

                  // Clean upcs field if it exists
                  if (cleanedItem.upcs) {
                    cleanedItem.upcs = cleanedItem.upcs.split(',').map(upc => upc.replace(/\D/g, '')).join(',');
                  }

                  // Handle the barcodes field which is a JSON string in the Amazon data
                  if (cleanedItem.barcodes) {
                    try {
                      // First attempt to parse it if it's a proper JSON string
                      const parsedBarcodes = JSON.parse(cleanedItem.barcodes.replace(/'/g, '"'));
                      // Clean each barcode in the array
                      const cleanedBarcodes = parsedBarcodes.map(barcode => barcode.replace(/\D/g, ''));
                      // Convert back to string format
                      cleanedItem.barcodes = JSON.stringify(cleanedBarcodes);
                    } catch (e) {
                      // If parsing fails, use regex to extract and clean barcode values
                      const barcodesMatch = cleanedItem.barcodes.match(/\["([^"]+)"\]/g);
                      if (barcodesMatch) {
                        const cleanedBarcodes = barcodesMatch.map(match => {
                          // Extract barcode from the match and clean it
                          const barcode = match.replace(/\["|"\]/g, '');
                          return barcode.replace(/\D/g, '');
                        });
                        cleanedItem.barcodes = JSON.stringify(cleanedBarcodes);
                      }
                    }
                  }

                  return cleanedItem;
                });

                if (cleanedAmazonData.length > 0 && !props.isMobile) {
                  let filteredData = cleanedAmazonData;
                  if (filteredData.length > 0) {
                    let filteredDataWithHide = filteredData.filter(prod => (
                      localStorage.getItem(prod["asin"].split("/")[prod["asin"].split("/").length - 1] + "NIU") === null ||
                      (new Date() > localStorage.getItem(prod["asin"].split("/")[prod["asin"].split("/").length - 1] + "NIU"))
                    ));
                    if (filteredDataWithHide.length > 0) {
                      setAzData(prevData => [...prevData, ...filteredDataWithHide]);
                    }
                  }
                } else {
                  setAzData(prevData => [...prevData, ...cleanedAmazonData]);
                }
              }

              // Check if we're done with all products
              if (index >= uniqueItems.length) {
                setIsProductsLoading(false);
              }
            })
            .catch(e => {
              setModalMsg("Error! Try again or contact us please.");
              setOpenModal(true);
              setLoading(false);
            });
        } catch (e) {
          setModalMsg("Error! Try again or contact us please.");
          setOpenModal(true);
          setLoading(false);
        }

        index += batchSize;

        // Set timeout for 30 seconds before processing next batch
        setTimeout(processBatch, 30000);
      } else {
        setIsProductsLoading(false);
      }
    }

    // Start processing batches
    processBatch();
  }

  function uploadCSV(file) {
    const lastClickTimestamp = localStorage.getItem('lastClickTimestamp') || -1;
    const minutesSinceLastClick = (Date.now() - parseInt(lastClickTimestamp)) / (1000 * 60);
    if (minutesSinceLastClick < 2 && lastClickTimestamp !== -1) {
      setLoading(false);
      setRefresh(true);
      setModalMsg(`Please wait ${((2 - minutesSinceLastClick) * 60).toFixed(0)} seconds before scanning again!`);
      setOpenModal(true);
      return
    }
    if (file.size > 10000000) {
      setLoading(false);
      setRefresh(true);
      setModalMsg("Your file is larger than 10MB. Please consider splitting the Excel file and sending multiple scan requests.");
      setOpenModal(true);
    } else {
      if (file) {
        const reader = new FileReader();

        reader.onload = function (e) {
          const fileName = file.name.toLowerCase();
          const fileIdentifier = `${fileName}_${Date.now()}`;
          sessionStorage.setItem("fileIdentifier", fileIdentifier);
          if (fileName.endsWith('.csv')) {
            const text = e.target.result;
            Papa.parse(text, {
              header: true,
              complete: (results) => {
                const headers = Object.keys(results.data[0] || {});
                const editData = results.data.map((item) => capitalizeKeys(item));
                if (editData.some(item => 'Barcode' in item)) {
                  localStorage.setItem('lastClickTimestamp', Date.now());
                  addScanedFile(fileName, editData.length, identifier, "both", fileIdentifier);
                  sendFile(editData);
                } else {
                  setCsvHeaders(headers);
                  setParsedData(editData);
                  setOpenColumnMappingModal(true);
                }
              },
            });
          } else if (fileName.endsWith('.xlsx')) {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const headers = json[0];
            const dataRows = json.slice(1).map((row) => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index];
              });
              return obj;
            });
            const editData = dataRows.map((item) => capitalizeKeys(item));
            if (editData.some(item => 'Barcode' in item)) {
              localStorage.setItem('lastClickTimestamp', Date.now());
              addScanedFile(fileName, editData.length, identifier, "both", fileIdentifier);
              sendFile(editData);
            } else {
              setCsvHeaders(headers);
              setParsedData(editData);
              setOpenColumnMappingModal(true);
            }
          }
        };

        if (file.name.toLowerCase().endsWith('.csv')) {
          reader.readAsText(file);
        } else if (file.name.toLowerCase().endsWith('.xlsx')) {
          reader.readAsArrayBuffer(file);
        }
      }
    }
  };

  const handleFileChange = (event) => {
    const files = event.target.files;

    if (files.length > 0) {
      setLoading(true);
      uploadCSV(files[0]);
      setCurrentFile(files[0]);
    } else {
      setLoading(false);
    }
  };

  const handleColumnMappingConfirm = (mapping) => {
    const identifierKey = Object.keys(parsedData[0] || {}).find(key => key.toLowerCase() === mapping.identifier.toLowerCase());
    const priceKey = Object.keys(parsedData[0] || {}).find(key => key.toLowerCase() === mapping.price.toLowerCase());

    const hasValidIdentifier = parsedData.some(item => {
      const value = item[identifierKey]?.toString() || '';
      const cleanValue = value.replace(/^['"]|['"]$/g, '').replace(/[-\s]/g, '');

      // For Both scanning, we only care about valid barcodes
      return cleanValue.length >= 8 && /^\d+$/.test(cleanValue);
    });

    const hasValidPrice = parsedData.some(item => {
      const value = item[priceKey]?.toString() || '';
      const cleanValue = value.replace(/^\$/, '').trim();
      return cleanValue && cleanValue !== "-1" && !isNaN(parseFloat(cleanValue));
    });

    if (!hasValidIdentifier || !hasValidPrice) {
      setInvalidColumns({
        identifier: !hasValidIdentifier,
        price: !hasValidPrice
      });
      return;
    }

    const mappedData = parsedData.map(item => {
      const nameKey = mapping.name ? Object.keys(item).find(key => key.toLowerCase() === mapping.name.toLowerCase()) : null;
      const imageKey = mapping.image ? Object.keys(item).find(key => key.toLowerCase() === mapping.image.toLowerCase()) : null;

      return {
        [identifier]: item[identifierKey],
        Price: item[priceKey]?.toString().trim() || "-1",
        Name: nameKey ? item[nameKey] : "",
        Image: imageKey ? item[imageKey] : ""
      };
    });

    const validData = mappedData.filter(item => {
      const identifierValue = item[identifier]?.toString() || '';
      const priceValue = item.Price;

      // Clean and validate barcode
      const cleanIdentifier = identifierValue.replace(/^['"]|['"]$/g, '').replace(/[-\s]/g, '');
      const isValidIdentifier = cleanIdentifier.length >= 8 && /^\d+$/.test(cleanIdentifier);

      // Clean and validate price
      const cleanPrice = priceValue.replace(/^\$/, '').trim();
      const isValidPrice = cleanPrice && cleanPrice !== "-1" && !isNaN(parseFloat(cleanPrice));

      if (isValidIdentifier) {
        item[identifier] = cleanIdentifier;
      }
      if (isValidPrice) {
        item.Price = cleanPrice;
      }

      return isValidIdentifier && isValidPrice;
    });

    if (validData.length === 0) {
      setOpenColumnMappingModal(false);
      setModalMsg("No valid data found! Please re-check your file and ensure the barcodes and prices are in the correct format.");
      setOpenModal(true);

      setTimeout(() => {
        setOpenColumnMappingModal(true);
      }, 500);
      return;
    }

    localStorage.setItem('lastClickTimestamp', Date.now());
    addScanedFile(currentFile.name, validData.length, identifier, "both", sessionStorage.getItem("fileIdentifier"));
    sendFile(validData);
    setOpenColumnMappingModal(false);
  };

  useEffect(() => {
    const checkAccess = async () => {
      fetch(
        `https://server.nepeto.com/aniseller_sub_check/${getUser()}/`,
        {
          mode: "cors",
        }
      )
        .then((response) => response.json())
        .then((data) => {
          setSubscribed(data);
        })
        .catch(() => {
        });
    };
    checkAccess();
  }, []);

  // First, add the CATEGORY_BSR_COUNT constant from CSVScanner
  const CATEGORY_BSR_COUNT = {
    'Kindle Store': 6756566, 'Video Shorts': 127580, 'Apps & Games': 936100, 'Baby Products': 4172515,
    'Digital Music': 68941656, 'Alexa Skills': 112529, 'Toys & Games': 9204843,
    'Patio, Lawn & Garden': 22608266, 'Books': 102997339, 'Arts, Crafts & Sewing': 16296162,
    'Software': 177161, 'Sports & Outdoors': 41416624, 'Gift Cards': 28409, 'Video Games': 1204647,
    'Handmade Products': 3644844, 'Clothing, Shoes & Jewelry': 275101033, 'Office Products': 12266328,
    'Grocery & Gourmet Food': 3869382, 'Tools & Home Improvement': 37997159, 'Movies & TV': 7740340,
    'Musical Instruments': 2893522, 'Collectibles & Fine Art': 8802597, 'Appliances': 1565048,
    'Pet Supplies': 8652837, 'Cell Phones & Accessories': 26169713, 'Baby': 4172515,
    'Industrial & Scientific': 20388882, 'Everything Else': 8317014, 'CDs & Vinyl': 8366789,
    'Beauty & Personal Care': 14890600, 'Home & Kitchen': 161989558, 'Electronics': 25856893,
    'Automotive': 46255283, 'Magazine Subscriptions': 42578, 'Health & Household': 11049107,
    'Audible Books & Originals': 743380, 'Kitchen & Dining': 37818304, "Amazon Devices": 1894,
    "Computers & Accessories": 14064999, "Camera & Photo Products": 4021127,
    "Collectible Coins": 210648, "Camera & Photo": 4021127
  };

  // Add the calculateROI function from CSVScanner
  function calculateROI(item, settings) {
    const costPrep = parseFloat(localStorage.getItem("costPrep") || 0);
    const price = parseFloat(item.price);
    const packMultiplier = parseFloat(item.pack) || 1;
    const totalPrice = price * packMultiplier;

    const profit = item.az_price - totalPrice -
      (settings.isFBM ? 0 : item.fba_fee) -
      (item.ref_fee_perc * item.az_price) -
      costPrep;

    const investment = totalPrice + costPrep;

    return (profit / investment) * 100;
  }

  // Add the filterData function from CSVScanner
  function filterData(data, filters) {
    return data.filter(item => {
      // noVariations
      if (parseInt(filters.noVariations || "0") !== 0 && item.variations !== "null") {
        return false;
      }

      // amazonNotSeller
      if (parseInt(filters.amazonNotSeller || "0") !== 0) {
        if (item.az_sells_it) {
          return false;
        }
      }

      // top_bsr
      if (filters.top_bsr && (filters.top_bsr[0] !== -1 || filters.top_bsr[1] !== -1)) {
        let categoryCount = CATEGORY_BSR_COUNT[item.category] || 10000000;
        let salesRankPercentage = (item.sales_rank / categoryCount) * 100;

        if (filters.top_bsr[0] !== -1 && salesRankPercentage < filters.top_bsr[0]) {
          return false;
        }

        if (filters.top_bsr[1] !== -1 && salesRankPercentage > filters.top_bsr[1]) {
          return false;
        }
      }

      // profit
      if (filters.profit && (filters.profit[0] !== -1 || filters.profit[1] !== -1)) {
        let profitValue;
        // Check if it's Amazon or Walmart data
        if (item?.asin !== undefined) {
          // Amazon
          const costPrep = parseFloat(localStorage.getItem("costPrep") || 0);
          const price = parseFloat(item.price) * (parseFloat(item.pack) || 1);
          const azPrice = item.az_price;
          const fbaCost = filters.isFBM ? 0 : item.fba_fee;
          const sellingOnAzFees = item.ref_fee_perc * item.az_price;
          profitValue = azPrice - price - fbaCost - costPrep - sellingOnAzFees;
        } else {
          // Walmart
          profitValue = item?.profit < 0 ? 0 : item?.profit;
        }

        if (profitValue === undefined) return false;
        if (filters.profit[0] !== -1 && profitValue < filters.profit[0]) {
          return false;
        }

        if (filters.profit[1] !== -1 && profitValue > filters.profit[1]) {
          return false;
        }
      }

      // roi
      if (filters.roi && (filters.roi[0] !== -1 || filters.roi[1] !== -1)) {
        if (item?.asin === undefined) {
          // Walmart
          const roi = parseFloat(item?.roi) < 0 ? 0 : parseFloat(item?.roi);
          if (filters.roi[0] !== -1 && roi < filters.roi[0]) {
            return false;
          }
          if (filters.roi[1] !== -1 && roi > filters.roi[1]) {
            return false;
          }
          if (item.roi === undefined || isNaN(item.roi)) return false;

        } else {
          // Amazon
          const roi = calculateROI(item, filters);
          if (filters.roi[0] !== -1 && roi < filters.roi[0]) {
            return false;
          }
          if (filters.roi[1] !== -1 && roi > filters.roi[1]) {
            return false;
          }
        }
      }

      // offers
      if (filters.offers && (filters.offers[0] !== -1 || filters.offers[1] !== -1)) {
        let offersCount = parseInt(item.total_offers_count) || 0;

        if (filters.offers[0] !== -1 && offersCount < filters.offers[0]) {
          return false;
        }
        if (filters.offers[1] !== -1 && offersCount > filters.offers[1]) {
          return false;
        }
      }

      // reviews
      if (filters.reviews && (filters.reviews[0] !== -1 || filters.reviews[1] !== -1)) {
        let reviews = parseInt(item.reviews) || 0;
        if (reviews === undefined) return false;
        if (filters.reviews[0] !== -1 && reviews < filters.reviews[0]) {
          return false;
        }
        if (filters.reviews[1] !== -1 && reviews > filters.reviews[1]) {
          return false;
        }
      }

      // search
      if (filters.search && filters.search !== "") {
        let searchLower = filters.search.toLowerCase();
        if (!item.title?.toLowerCase().includes(searchLower) && !item.asin?.includes(filters.search)) {
          return false;
        }
      }

      // category
      if (filters.category && filters.category !== "Category" && !filters.category.includes(item.category)) {
        return false;
      }

      // qty, moq, bsr
      for (let filterAttr of ["qty", "moq", "bsr"]) {
        if (filters[filterAttr] && (filters[filterAttr][0] !== -1 || filters[filterAttr][1] !== -1)) {
          let value = item[filterAttr === "bsr" ? "sales_rank" : filterAttr];

          if (filters[filterAttr][0] !== -1 && value < filters[filterAttr][0]) {
            return false;
          }

          if (filters[filterAttr][1] !== -1 && value > filters[filterAttr][1]) {
            return false;
          }
        }
      }

      // price
      if (filters.price && (filters.price[0] !== -1 || filters.price[1] !== -1)) {
        let price = item.price * (1 - ((filters.priceCoupon || 0) / 100));

        if (filters.price[0] !== -1 && price < filters.price[0]) {
          return false;
        }

        if (filters.price[1] !== -1 && price > filters.price[1]) {
          return false;
        }
      }

      // sales
      if (filters.sales && (filters.sales[0] !== -1 || filters.sales[1] !== -1)) {
        let sales = item.keepa_monthly_sold > 0 ? item.keepa_monthly_sold : item.estimate_sales_30_days;

        if (filters.sales[0] !== -1 && sales < filters.sales[0]) {
          return false;
        }

        if (filters.sales[1] !== -1 && sales > filters.sales[1]) {
          return false;
        }
      }

      // bsr drops 30 days
      if (filters.bsr_drops_30_days && (filters.bsr_drops_30_days[0] !== -1 || filters.bsr_drops_30_days[1] !== -1)) {
        let drops = item.sales_rank_drops_30_days;

        if (filters.bsr_drops_30_days[0] !== -1 && drops < filters.bsr_drops_30_days[0]) {
          return false;
        }

        if (filters.bsr_drops_30_days[1] !== -1 && drops > filters.bsr_drops_30_days[1]) {
          return false;
        }
      }

      return true;
    });
  }

  // Amazon filtering useEffect
  useEffect(() => {
    if (azData.length > 0) {
      const filteredAmazonData = filterData(azData, azFilters);

      // Sort function for Amazon data
      const sortAmazonData = (a, b) => {
        if (azFilters.sortBy) {
          let getKey = (n, key) => {
            if (azFilters.sortBy[0] === "roi") {
              return parseFloat(calculateROI(n, azFilters));
            }
            if (azFilters.sortBy[0] === "profit") {
              return parseFloat(n.az_price - (n.price * (1 - ((azFilters.priceCoupon || 0) / 100))) -
                (azFilters.isFBM ? 0 : n.fba_fee) - (n.ref_fee_perc * n.az_price) -
                parseFloat(localStorage.getItem("costPrep") || 0));
            }
            return parseFloat(n[key])
          }
          if (azFilters.sortBy[1]) {
            return getKey(a, azFilters.sortBy[0]) - getKey(b, azFilters.sortBy[0]);
          } else {
            return getKey(b, azFilters.sortBy[0]) - getKey(a, azFilters.sortBy[0]);
          }
        } else {
          return 1;
        }
      };

      setAzFilteredData(filteredAmazonData.sort(sortAmazonData));

      // Handle saving results if needed
      if (identifier && saveResult) {
        let savedState = JSON.stringify([{
          selectedStore: "amazon",
          identifier: identifier,
          data: JSON.stringify(azData),
          filters: JSON.stringify(azFilters) || "{}"
        }]);

        let email = getUser();
        addScanResultsToDB(email, savedState, sessionStorage.getItem("fileIdentifier") + "_amazon");
      }
    } else {
      setAzFilteredData([]);
    }
  }, [azFilters, azData, saveResult]);

  // Walmart filtering useEffect
  useEffect(() => {
    if (wmData.length > 0) {
      const filteredWalmartData = filterData(wmData, wmFilters);

      // Sort function for Walmart data
      const sortWalmartData = (a, b) => {
        if (wmFilters.sortBy) {
          let getKey = (n, key) => {
            if (wmFilters.sortBy[0] === "roi") {
              return parseFloat(n.roi || 0);
            }
            if (wmFilters.sortBy[0] === "profit") {
              return parseFloat(n.profit || 0);
            }
            return parseFloat(n[key] || 0)
          }
          if (wmFilters.sortBy[1]) {
            return getKey(a, wmFilters.sortBy[0]) - getKey(b, wmFilters.sortBy[0]);
          } else {
            return getKey(b, wmFilters.sortBy[0]) - getKey(a, wmFilters.sortBy[0]);
          }
        } else {
          return 1;
        }
      };

      setWmFilteredData(filteredWalmartData.sort(sortWalmartData));

      // Handle saving results if needed
      if (identifier && saveResult) {
        let savedState = JSON.stringify([{
          selectedStore: "walmart",
          identifier: identifier,
          data: JSON.stringify(wmData),
          filters: JSON.stringify(wmFilters) || "{}"
        }]);

        let email = getUser();
        addScanResultsToDB(email, savedState, sessionStorage.getItem("fileIdentifier") + "_walmart");
      }
    } else {
      setWmFilteredData([]);
    }
    lastUpdated = Date.now();
  }, [wmFilters, wmData, saveResult]);

  return (
    <>
      {azData.length === 0 && wmData.length === 0 ? (
        <>
          <div style={{ marginBottom: "3.5vh" }}>
            <h1
              style={{
                fontSize: props.isMobile ? "3vh" : "2vw",
                fontWeight: "380",
                textAlign: "center",
                color: "#7e3e1b",
                marginBottom: props.isMobile && "2vh",
              }}
            >
              Both Marketplaces Scanner
            </h1>
            <h2
              style={{
                fontSize: props.isMobile ? "1.5vh" : "1vw",
                fontWeight: "380",
                textAlign: "center",
                color: "rgb(230,50,50)",
                marginBottom: props.isMobile && "2vh",
              }}
            >
              The file should include the following: Barcode (UPC/EAN), Cost (Buy Cost), Name (optional), Image (optional).
              <br /><strong>Download and use our template to ensure it works as expected!</strong>
            </h2>
          </div>
          <div style={{ width: "50vw", margin: "auto", alignItems: "center", textAlign: "center", backgroundColor: "rgba(200,200,200,0.2)", borderRadius: "30px", marginTop: "1vh", padding: "2vh 1vw 2vh 1vw" }}>
            <h2 style={{ marginBottom: "2vh" }}>Upload the CSV file</h2>
            <h3 style={{ marginBottom: "2vh" }}>Make sure you use our template or use the same column names!</h3>
            <span onClick={() => window.location.href = `data:text/csv;charset=utf-8,Barcode,Cost,Name,Image\n`} style={{ color: "slateblue", textDecoration: "underline", cursor: "pointer", fontSize: "150%", fontWeight: "600" }}>Click here to download the template</span>

            <Box sx={{ border: '2px dashed #ccc', padding: '16px', textAlign: 'center', width: '30%', borderRadius: '8px', cursor: 'pointer', margin: '25px auto', }}>
              <Button component="label" variant="contained" startIcon={<CloudUploadIcon />} size="large" style={{
                textTransform: 'none',
                fontFamily: 'RobotoSlab',
                color: '#FCF8F3',
                borderRadius: '10px',
                backgroundColor: '#7e3e1b',
                border: '1px solid #FCF8F3',
              }}>
                Choose a file
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".csv,.xlsx"
                  style={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                />
              </Button>
              <Typography variant="body2" sx={{ marginTop: '8px', fontFamily: 'RobotoSlab', }}>
                (Accept .csv and .xlsx files)
              </Typography>

              {loading ?
                <div style={{ color: '#7e3e1b', }}>
                  <LinearProgress sx={{ marginTop: '16px' }} color={'inherit'} />
                </div>
                :
                <Typography variant="caption" sx={{ display: 'block', marginTop: '8px', fontFamily: 'RobotoSlab', }}>
                  No file uploaded
                </Typography>
              }
            </Box>
          </div>
        </>
      ) : (
        <>
          {/* Amazon Filter */}
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ textAlign: "center", color: "#ff9900", marginBottom: "10px", fontSize: "1.3rem" }}>
              Amazon Filters
            </h3>
            {azData && azData.length > 0 && <FilterBar
              data={azData}
              setFilters={setAzFilters}
              filters={azFilters}
              activePlanLevel={props.activePlanLevel}
              saveResult={saveResult}
              setSaveResult={setSaveResult}
              loadedFile={true}
              csvRows={csvRows}
            />}
          </div>

          {/* Walmart Filter */}
          <div style={{ marginBottom: "20px" }}>
            <h3 style={{ textAlign: "center", color: "#0071dc", marginBottom: "10px", fontSize: "1.3rem" }}>
              Walmart Filters
            </h3>
            {wmData && wmData.length > 0 && <WalmartFilterBar
              data={wmData}
              setFilters={setWmFilters}
              filters={wmFilters}
              activePlanLevel={props.activePlanLevel}
              saveResult={saveResult}
              setSaveResult={setSaveResult}
              loadedFile={true}
              csvRows={csvRows}
            />}
          </div>

          {/* Combined Products Table */}
          <BothProductsTable
            amazonData={azFilteredData} // Changed from azData to azFilteredData
            walmartData={wmFilteredData} // Changed from wmData to wmFilteredData
            setAmazonData={setAzData}
            setWalmartData={setWmData}
            isMobile={props.isMobile}
            hideCredits={props.hideCredits}
            isProductsLoading={isProductsLoading}
            amazonFilters={azFilters}
            walmartFilters={wmFilters}
            setAmazonFilters={setAzFilters}
            setWalmartFilters={setWmFilters}
            amazonSortASC={azSortASC}
            walmartSortASC={wmSortASC}
            setAmazonSortASC={setAzSortASC}
            setWalmartSortASC={setWmSortASC}
            identifier={identifier}
            sellerId={props.sellerId}
            refreshToken={props.refreshToken}
          />
        </>
      )}

      {/* Standard alert modal */}
      <Modal open={openModal} onClose={() => { setOpenModal(false); if (refresh) window.location.reload(); }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '26vw',
            bgcolor: '#FCF8F3',
            borderRadius: 2,
            border: '4px solid rgba(126, 62, 27, 0.5)',
            boxShadow: 24,
            p: 4,
            outline: 'none',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom style={{ fontFamily: 'RobotoSlab', textAlign: 'center', }}>
            {modalMsg}
          </Typography>

          {/* OK button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
            <Button variant="contained" onClick={() => { setOpenModal(false); if (refresh) window.location.reload(); }} style={{
              textTransform: 'none',
              fontFamily: 'RobotoSlab',
              color: '#FCF8F3',
              borderRadius: '20px',
              backgroundColor: '#769358',
              border: '1px solid #FCF8F3'
            }} >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Subscription alert modal */}
      <Modal open={openAlertModal} onClose={() => setOpenAlertModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '26vw',
            bgcolor: '#FCF8F3',
            borderRadius: 2,
            border: '4px solid rgba(126, 62, 27, 0.5)',
            boxShadow: 24,
            p: 4,
            outline: 'none',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom style={{ fontFamily: 'RobotoSlab', textAlign: 'center', }}>
            {alertModalMsg}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
            <Button variant="contained" onClick={() => { setOpenAlertModal(false); navigate(navigatePage) }} style={{
              textTransform: 'none',
              fontFamily: 'RobotoSlab',
              color: '#FCF8F3',
              borderRadius: '20px',
              backgroundColor: '#769358',
              border: '1px solid #FCF8F3'
            }} >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Column mapping modal */}
      <Modal open={openColumnMappingModal} onClose={() => setOpenColumnMappingModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '26vw',
          bgcolor: '#FCF8F3',
          borderRadius: 2,
          border: '4px solid rgba(126, 62, 27, 0.5)',
          boxShadow: 24,
          p: 4,
          outline: 'none',
        }}>
          <Typography variant="h6" component="h2" gutterBottom style={{ fontFamily: 'RobotoSlab', textAlign: 'center' }}>
            Map Your Columns
          </Typography>
          <ColumnMappingContent
            csvHeaders={csvHeaders}
            invalidColumns={invalidColumns}
            setInvalidColumns={setInvalidColumns}
            onConfirm={handleColumnMappingConfirm}
            onCancel={() => setOpenColumnMappingModal(false)}
          />
        </Box>
      </Modal>
    </>
  );
}

// Extract the column mapping content as a separate component for clarity
const ColumnMappingContent = ({ csvHeaders, invalidColumns, setInvalidColumns, onConfirm, onCancel }) => {
  const [mapping, setMapping] = useState({
    identifier: '',
    price: '',
    name: '',
    image: ''
  });

  const handleMappingChange = (field, value) => {
    setMapping(prev => ({
      ...prev,
      [field]: value
    }));
    setInvalidColumns(prev => ({
      ...prev,
      [field]: false
    }));
  };

  useEffect(() => {
    // Set default mapping values based on column headers
    const defaultMapping = {
      identifier: csvHeaders.find(h => {
        const lowerHeader = h.toLowerCase();
        return lowerHeader.includes('barcode') || lowerHeader.includes('upc') || lowerHeader.includes('ean');
      }) || '',
      price: csvHeaders.find(h => h.toLowerCase().includes('price') || h.toLowerCase().includes('cost')) || '',
      name: csvHeaders.find(h => h.toLowerCase().includes('name')) || '',
      image: csvHeaders.find(h => h.toLowerCase().includes('image')) || ''
    };
    setMapping(defaultMapping);
  }, [csvHeaders]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth error={invalidColumns.identifier}>
          <InputLabel>Barcode Column (UPC/EAN)</InputLabel>
          <Select
            value={mapping.identifier}
            label="Barcode Column (UPC/EAN)"
            onChange={(e) => handleMappingChange('identifier', e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': invalidColumns.identifier ? {
                borderColor: 'error.main',
                borderWidth: '2px'
              } : {}
            }}
          >
            {csvHeaders.map((header) => (
              <MenuItem key={header} value={header}>{header}</MenuItem>
            ))}
          </Select>
          {invalidColumns.identifier && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
              Invalid barcode format. Must be 8 or more digits.
            </Typography>
          )}
        </FormControl>
        <FormControl fullWidth error={invalidColumns.price}>
          <InputLabel>Price Column</InputLabel>
          <Select
            value={mapping.price}
            label="Price Column"
            onChange={(e) => handleMappingChange('price', e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': invalidColumns.price ? {
                borderColor: 'error.main',
                borderWidth: '2px'
              } : {}
            }}
          >
            {csvHeaders.map((header) => (
              <MenuItem key={header} value={header}>{header}</MenuItem>
            ))}
          </Select>
          {invalidColumns.price && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
              Invalid price format. Must be a valid number.
            </Typography>
          )}
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Name Column (Optional)</InputLabel>
          <Select
            value={mapping.name}
            label="Name Column (Optional)"
            onChange={(e) => handleMappingChange('name', e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {csvHeaders.map((header) => (
              <MenuItem key={header} value={header}>{header}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Image Column (Optional)</InputLabel>
          <Select
            value={mapping.image}
            label="Image Column (Optional)"
            onChange={(e) => handleMappingChange('image', e.target.value)}
          >
            <MenuItem value="">None</MenuItem>
            {csvHeaders.map((header) => (
              <MenuItem key={header} value={header}>{header}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
        <Button
          variant="contained"
          onClick={() => {
            if (mapping.identifier && mapping.price) {
              onConfirm(mapping);
            }
          }}
          disabled={!mapping.identifier || !mapping.price}
          style={{
            textTransform: 'none',
            fontFamily: 'RobotoSlab',
            color: '#FCF8F3',
            borderRadius: '20px',
            backgroundColor: '#769358',
            border: '1px solid #FCF8F3'
          }}
        >
          Confirm
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          style={{
            textTransform: 'none',
            fontFamily: 'RobotoSlab',
            color: '#769358',
            borderRadius: '20px',
            border: '1px solid #769358'
          }}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default BothMarketplaceScan;